import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle, Bell, Users, Flame, CalendarClock, X } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import Navbar from "../components/Navbar";
import SignalBadge from "../components/SignalBadge";
import TierBadge from "../components/TierBadge";
import AlertClassBadge from "../components/AlertClassBadge";
import AlertStatusBadge from "../components/AlertStatusBadge";
import PriceContinuum from "../components/PriceContinuum";
import MoonbucksScoreCard from "../components/MoonbucksScoreCard";
import UpsideAnchorCard from "../components/UpsideAnchorCard";
import EntryThesisCard from "../components/EntryThesisCard";
import { useLivePrices, mergeLiveIntoAlert } from "../hooks/useLivePrices";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function StockDetail() {
  const { symbol } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/stocks/${symbol}`);
      setData(r.data);
    } catch (e) {
      console.error("stock detail load failed", e);
    }
    setLoading(false);
  }, [symbol]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll live prices for THIS symbol every 30s so the top-of-page ticker,
  // the ActiveAlertBanner P&L, and the entry-zone hint all update without
  // the user having to reload. Must be called BEFORE any conditional
  // returns so React's hook order stays stable across renders.
  const { prices: livePrices, lastFetch: liveLastFetch } = useLivePrices([symbol]);
  const liveTick = livePrices[(symbol || "").toUpperCase()];

  if (loading) return <ShellLoading />;
  if (!data) return <ShellEmpty />;

  const { indicators: ind, decision, history, regime, conviction, sentiment } = data;
  const convictionStack = data.conviction_stack;
  const primaryClass = data.primary_class || "steady";
  const isMoonHeadline = primaryClass === "moonshot";
  const blockers = data.blockers || [];
  const steadyScreen = data.steady?.screen;
  const moonScreen = data.moonshot?.screen;
  const activeAlert = data.active_alert;
  const isAdmin = user?.role === "admin";

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Link to="/app/screener" className="inline-flex items-center text-sm text-neutral-600 hover:text-mb-emerald mb-6" data-testid="back-to-screener">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to screener
        </Link>

        <div className="flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
          <div>
            <div className="label-uppercase text-mb-emerald">Stock detail</div>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="font-display font-semibold text-5xl tracking-tight font-mono-data">{symbol}</h1>
              {data.tier && <TierBadge tier={data.tier} upside={data.upside} className="mt-2" />}
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="font-mono-data text-3xl font-bold" data-testid="hero-price">
                ${liveTick?.price ?? ind.last_price}
              </span>
              {typeof (liveTick?.change_pct ?? ind.change_today_pct) === "number" && (
                <span
                  className={`font-mono-data text-sm px-2 py-0.5 rounded-md border ${
                    (liveTick?.change_pct ?? ind.change_today_pct) >= 0
                      ? "text-buy border-emerald-200 bg-emerald-50"
                      : "text-sell border-red-200 bg-red-50"
                  }`}
                  data-testid="change-today"
                  title="Today's session change vs previous close"
                >
                  {(liveTick?.change_pct ?? ind.change_today_pct) >= 0 ? "▲" : "▼"}{" "}
                  {(liveTick?.change_pct ?? ind.change_today_pct) >= 0 ? "+" : ""}{(liveTick?.change_pct ?? ind.change_today_pct)}%{" "}
                  <span className="text-[10px] tracking-wider uppercase opacity-60">today</span>
                </span>
              )}
              <span className={`font-mono-data text-sm ${ind.momentum_5d >= 0 ? "text-buy" : "text-sell"}`}>
                {ind.momentum_5d >= 0 ? "▲" : "▼"} {ind.momentum_5d}% (5d)
              </span>
              {liveTick && (
                <span
                  className="inline-flex items-center gap-1 font-mono-data text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700"
                  title="Live price via Finnhub, updated every 30s"
                  data-testid="live-tick-indicator"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  LIVE
                </span>
              )}
            </div>
          </div>
          <SignalBadge action={decision.action} confidence={decision.confidence} size="lg" />
        </div>

        {/* Active alert banner — only when this ticker has a published alert (active or recently closed) */}
        {activeAlert && <ActiveAlertBanner alert={activeAlert} isAdmin={isAdmin} onClosed={fetchData} symbol={symbol} livePrices={livePrices} />}

        <EntryThesisCard thesis={activeAlert?.entry_thesis} rechecks={activeAlert?.rechecks} />

        {/* Moonbucks Score — the headline number subscribers should look at first */}
        <div className="mt-6">
          <MoonbucksScoreCard grade={data.moonbucks_grade} />
        </div>

        {/* Upside anchor — analyst consensus + model-estimated 45-day range.
            Answers the "how far can it go?" question that TP1/moon caps used
            to fake with static percentages. Real Wall St + our own math. */}
        <UpsideAnchorCard
          anchor={activeAlert?.upside_anchor || data.upside_anchor}
          entryLabel={activeAlert ? "Entry" : "Current"}
        />

        {/* Decision card — colors flex to match the primary strategy that fired (or steady when no-trade) */}
        <div
          className={`mt-6 rounded-lg p-6 text-white ${isMoonHeadline ? "bg-gradient-to-br from-pink-500 to-pink-700" : "bg-mb-emerald"}`}
          data-testid="decision-card"
        >
          {activeAlert && (
            <div className="mb-4 rounded-md bg-white/15 border border-white/25 px-3 py-2 text-xs text-white/95 leading-relaxed" data-testid="decision-card-reconcile-note">
              Original alert is still open — see the banner above for entry/TP/stop and live P&amp;L. The card below shows what the live scanner sees on this ticker <em>right now</em>, which may differ from the alert&apos;s original setup.
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="label-uppercase text-white/85">Trade decision</span>
              <AlertClassBadge alertClass={primaryClass} compact />
            </div>
            <div className="text-[10px] font-mono-data text-white/80 tracking-wider uppercase">
              evaluated against both Steady &amp; Moonshot
            </div>
          </div>
          <p className="mt-2 text-lg leading-relaxed">{decision.reason}</p>

          {/* Blockers — only when neither strategy is firing BUY */}
          {blockers.length > 0 && (
            <div className="mt-4 bg-white/15 border border-white/20 rounded-xl p-4" data-testid="no-trade-blockers">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="label-uppercase text-white">Why not yet</span>
              </div>
              <ul className="space-y-2">
                {blockers.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`blocker-${b.kind}`}>
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-md flex-shrink-0 ${
                      b.severity === "high" ? "bg-red-300" :
                      b.severity === "medium" ? "bg-amber-300" :
                      "bg-white/80"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white">{b.label}</div>
                      <div className="text-white/85 text-xs mt-0.5">{b.hint}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 font-mono-data text-sm">
            <KV label="Action" val={decision.action} />
            <KV label="Confidence" val={`${Math.round(decision.confidence * 100)}%`} />
            <KV label="Regime" val={regime.regime.toUpperCase()} />
            <KV label="Conviction" val={conviction ? `${conviction.score > 0 ? "+" : ""}${conviction.score}` : ind.conviction_score ?? "—"} />
          </div>
        </div>

        {/* SENTIMENT CARD — real news-based via LLM */}
        {sentiment && (
          <div className="mt-6 bg-white border border-mb-border rounded-lg p-6" data-testid="news-sentiment-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  sentiment.label === "bullish" ? "bg-emerald-100 text-emerald-700" :
                  sentiment.label === "bearish" ? "bg-red-100 text-red-700" :
                  "bg-neutral-100 text-neutral-600"
                }`} data-testid="sentiment-score-badge">
                  <span className="font-mono-data font-extrabold text-base">
                    {sentiment.score > 0 ? "+" : ""}{sentiment.score}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="label-uppercase text-neutral-500">News Sentiment</span>
                    <span className={`label-uppercase text-[9px] px-2 py-0.5 rounded-md ${
                      sentiment.label === "bullish" ? "bg-emerald-100 text-emerald-700" :
                      sentiment.label === "bearish" ? "bg-red-100 text-red-700" :
                      "bg-neutral-100 text-neutral-600"
                    }`} data-testid="sentiment-label">{sentiment.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-800 leading-relaxed" data-testid="sentiment-summary">{sentiment.summary}</p>
                  <div className="mt-2 font-mono-data text-[10px] text-neutral-400">
                    {sentiment.headlines_used} headlines · {sentiment.source === "llm" ? "Claude AI" : sentiment.source} ·
                    {sentiment.scored_at ? ` ${new Date(sentiment.scored_at).toLocaleTimeString()}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONVICTION STACK — Smart-money signals (insider buying + squeeze + catalyst) */}
        {convictionStack && !convictionStack.error && (
          <ConvictionStackCard stack={convictionStack} />
        )}

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* CHART */}
          <div className="lg:col-span-2 bg-white border border-mb-border rounded-lg p-6">
            <div className="label-uppercase text-neutral-500">90-day price</div>
            <div className="h-72 mt-4 w-full" style={{ minHeight: 288 }} data-testid="price-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  {/* Softer grid + darker axis ink for way better contrast against
                      the bright magenta line (matches the Trade Decision card
                      accent so the eye keeps its bearings between the two). */}
                  <CartesianGrid strokeDasharray="3 4" stroke="#e2e2e6" />
                  <XAxis dataKey="date" stroke="#525260" fontSize={10} tick={{ fontFamily: "Inter", fill: "#525260" }} />
                  <YAxis stroke="#525260" fontSize={10} tick={{ fontFamily: "Inter", fill: "#525260" }} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 8, color: "#fff", fontFamily: "Inter", fontSize: 12 }}
                    itemStyle={{ color: "#FF4D9D" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="close"
                    stroke="#FF4D9D"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5, fill: "#ffffff", stroke: "#FF4D9D", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* INDICATORS */}
          <div className="bg-white border border-mb-border rounded-lg p-6">
            <div className="label-uppercase text-neutral-500">Indicators</div>
            <ul className="mt-4 space-y-3 text-sm">
              <Row label="RSI (14)" val={ind.rsi} />
              <Row label="MACD bullish" val={ind.macd_bullish ? "✓ YES" : "—"} />
              <Row label="50-day SMA" val={`$${ind.sma50}`} />
              <Row label="200-day SMA" val={`$${ind.sma200}`} />
              <Row label="20-day high" val={`$${ind.high_20d}`} />
              <Row label="52-week high" val={`$${ind.high_52w}`} />
              <Row label="Volume ratio" val={`${ind.volume_ratio}×`} />
              <Row label="Conviction (technical)" val={ind.conviction_score ?? ind.sentiment_score ?? 0} />
            </ul>
          </div>
        </div>

        {/* Both strategies — Pressure Index (Steady) AND Pre-Breakout Index (Moonshot) */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {steadyScreen && (
            <ScreenCard
              title="Moonbucks Pressure Index"
              subtitle="Steady tier · 5 of 6 must fire"
              accent="emerald"
              screen={steadyScreen}
              passLabel="A-grade setup"
              failLabel="Below A-grade threshold"
              minToFire={5}
            />
          )}
          {moonScreen && (
            <ScreenCard
              title="Pre-Breakout Index"
              subtitle="Moonshot tier · 2 of 3 must fire"
              accent="pink"
              screen={moonScreen}
              passLabel="Coil firing"
              failLabel="Coil still loose"
              minToFire={2}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const KV = ({ label, val }) => (
  <div>
    <div className="text-[10px] text-white/85 tracking-widest uppercase font-bold">{label}</div>
    <div className="text-lg mt-0.5">{val}</div>
  </div>
);

function ScreenCard({ title, subtitle, accent, screen, passLabel, failLabel, minToFire }) {
  const isPink = accent === "pink";
  const passes = screen.score >= minToFire;
  const dotActive = isPink ? "bg-pink-500" : "bg-mb-emerald";
  const bgActive = isPink ? "bg-pink-50 border-pink-200" : "bg-mb-emerald/5 border-mb-emerald/30";
  const textActive = isPink ? "text-pink-700" : "text-mb-emerald";
  const stateColor = passes ? textActive : "text-neutral-400";
  return (
    <div className="bg-white border border-mb-border rounded-lg p-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="label-uppercase text-neutral-500">{title}</div>
          <h3 className="font-display font-semibold text-xl mt-0.5">
            {screen.score}/{screen.max_score} components active
          </h3>
          <div className="text-[11px] text-neutral-500 mt-0.5 font-mono-data tracking-wide">{subtitle}</div>
        </div>
        <div className={`label-uppercase ${stateColor} text-right`}>
          {passes ? passLabel : failLabel}
        </div>
      </div>
      <ul className="mt-4 space-y-2" data-testid={`screen-list-${accent}`}>
        {screen.signals.map((s) => (
          <li
            key={s.name}
            className={`flex items-start gap-3 p-3 rounded-lg border transition ${
              s.fired ? bgActive : "bg-neutral-50 border-mb-border opacity-60"
            }`}
            data-testid={`component-${s.name.toLowerCase().replace(/ /g, '-')}`}
          >
            <span className={`w-2 h-2 rounded-full mt-2 ${s.fired ? dotActive : "bg-neutral-300"}`} />
            <div className="flex-1 flex items-center justify-between">
              <div>
                <div className={`font-semibold text-sm ${s.fired ? "text-neutral-900" : "text-neutral-500"}`}>
                  {s.name}
                </div>
                <div className={`font-mono-data text-xs mt-0.5 ${s.fired ? textActive : "text-neutral-400"}`}>
                  {s.detail}
                </div>
              </div>
              <span className={`label-uppercase text-[10px] ${s.fired ? textActive : "text-neutral-400"}`}>
                {s.fired ? "Active" : "Idle"}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
const Row = ({ label, val }) => (
  <li className="flex items-center justify-between border-b last:border-0 border-neutral-100 pb-2">
    <span className="text-neutral-600">{label}</span>
    <span className="font-mono-data font-semibold">{val}</span>
  </li>
);
const ShellLoading = () => (
  <div className="bg-mb-sand min-h-screen">
    <Navbar />
    <div className="max-w-7xl mx-auto px-6 py-20 text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-mb-emerald" />
    </div>
  </div>
);
const ShellEmpty = () => (
  <div className="bg-mb-sand min-h-screen">
    <Navbar />
    <div className="max-w-7xl mx-auto px-6 py-20 text-center text-neutral-500">No data found.</div>
  </div>
);

// Banner that surfaces an existing published alert for this ticker.
// Live status (zone / win / loss / expired) + days remaining + live P&L.
function ActiveAlertBanner({ alert, isAdmin, onClosed, symbol, livePrices }) {
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState("");
  const [showReasonBox, setShowReasonBox] = useState(false);
  const live = mergeLiveIntoAlert(alert, livePrices);
  const cls = alert.alert_class || "steady";
  const isMoon = cls === "moonshot";
  const isActive = live.status === "ACTIVE";
  const isWin = String(live.status || "").startsWith("WIN");
  const isLoss = String(live.status || "").startsWith("LOSS");
  const ret = live.return_pct ?? 0;

  const submitClose = async () => {
    if (!window.confirm(`Force-close ${symbol} now and blast the exit notification to every subscriber (Discord + email)?`)) return;
    setClosing(true);
    try {
      const url = reason.trim()
        ? `/admin/alerts/${alert.id}/close?reason=analyst_manual&note=${encodeURIComponent(reason.trim())}`
        : `/admin/alerts/${alert.id}/close?reason=analyst_manual`;
      const r = await api.post(url);
      const pct = r.data?.final_return_pct;
      toast.success(`${symbol} closed · ${pct != null ? (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%" : "notified"}`);
      setShowReasonBox(false);
      setReason("");
      onClosed && onClosed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally {
      setClosing(false);
    }
  };

  // Bar fill — visualize where current price sits between Stop and Moon target
  const entry = alert.entry_price || 0;
  const tp1 = alert.take_profit_price || 0;
  const moon = alert.moon_target_price || tp1;
  const stop = alert.stop_loss_price || 0;
  const now = live.current_price ?? entry;

  // Banner background hints at status without being heavy
  const bg = isActive
    ? (isMoon ? "bg-pink-50 border-pink-200" : "bg-mb-emerald/5 border-mb-emerald/30")
    : isWin
    ? "bg-emerald-50 border-emerald-200"
    : isLoss
    ? "bg-red-50 border-red-200"
    : "bg-neutral-50 border-mb-border";

  const created = alert.created_at ? new Date(alert.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

  return (
    <div className={`mt-6 rounded-lg border-2 p-5 ${bg}`} data-testid="active-alert-banner">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Bell className={`w-4 h-4 ${isMoon ? "text-pink-600" : "text-mb-emerald"}`} />
          <span className="label-uppercase text-neutral-600">Moonbucks alert</span>
          <AlertClassBadge alertClass={cls} compact />
          <AlertStatusBadge live={live} alertClass={cls} compact />
        </div>
        <div className="flex items-center gap-3 text-right">
          <span className={`font-mono-data font-extrabold text-2xl ${ret >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {ret >= 0 ? "+" : ""}{ret}%
          </span>
          {isActive && live.days_remaining != null && (
            <span
              className={`font-mono-data text-[11px] tracking-wider uppercase px-2 py-1 rounded ${live.days_remaining <= 2 ? "bg-amber-100 text-amber-800" : "bg-purple-100"}`}
              style={live.days_remaining <= 2 ? undefined : { color: "var(--mb-emerald-dark)" }}
              data-testid="banner-days-left"
              title={`Day ${live.days_elapsed} of ${live.max_hold_days}`}
            >
              {live.days_remaining}d left
            </span>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
        Published <span className="font-semibold">{created}</span> at{" "}
        <span className="font-mono-data font-semibold">${alert.entry_price}</span>.{" "}
        Now <span className="font-mono-data font-semibold">${Number(live.current_price ?? alert.entry_price).toFixed(2)}</span>{" "}
        {isActive ? (
          live.entry_zone === "in_zone"
            ? "— still in the entry zone."
            : live.entry_zone === "extended"
            ? "— extended past entry, smaller R:R if you take it now."
            : live.tp1_hit
            ? "— TP1 already hit, runner phase. Don't enter new."
            : live.entry_zone === "chasing"
            ? "— price has run past the alert. Don't chase."
            : "— price is below entry, wait or skip."
        ) : isWin ? (
          live.status === "WIN_MOON" ? "— closed at MOON target." :
          live.status === "WIN_TIME" ? "— closed positive at hold expiry." : "— closed at first target."
        ) : isLoss ? (
          live.status === "LOSS_SL" ? "— stopped out." : "— closed negative at hold expiry."
        ) : ""}
      </p>

      {/* Range bar: Stop ──── Entry ── Now ── TP1 ──── Moon */}
      {entry > 0 && stop > 0 && moon > entry && (
        <div className="mt-4">
          <PriceContinuum entry={entry} stop={stop} tp1={tp1} moon={moon} now={now} />
        </div>
      )}

      {/* Admin-only manual close — analyst reviews the chart and calls it */}
      {isAdmin && isActive && (
        <div className="mt-4 pt-4 border-t border-mb-border" data-testid="stock-detail-close-panel">
          {!showReasonBox ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] font-mono-data tracking-widest uppercase text-neutral-600">
                Analyst desk · manual override
              </div>
              <Button
                variant="outline"
                onClick={() => setShowReasonBox(true)}
                className="text-red-600 border-red-300 hover:bg-red-50 rounded-md text-xs"
                data-testid="stock-detail-close-btn"
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Close alert now
              </Button>
            </div>
          ) : (
            <div className="rounded-md bg-red-50/60 border border-red-200 p-3" data-testid="stock-detail-close-reason-box">
              <div className="text-xs font-semibold text-red-800 mb-2">
                Send an exit notification for {symbol} to every subscriber?
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional analyst note — e.g. 'Momentum broke on daily, taking profit here.' Included in the email + Discord broadcast."
                rows={2}
                className="w-full text-xs p-2 rounded border border-red-200 bg-white font-mono-data"
                data-testid="stock-detail-close-reason-input"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowReasonBox(false); setReason(""); }}
                  disabled={closing}
                  className="rounded-md text-xs"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitClose}
                  disabled={closing}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-md text-xs"
                  data-testid="stock-detail-close-confirm"
                >
                  {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <X className="w-3.5 h-3.5 mr-1.5" />}
                  Close &amp; notify {live.days_elapsed != null ? `(day ${live.days_elapsed})` : ""}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────── Conviction Stack — smart-money signal trio ─────────────────
function ConvictionStackCard({ stack }) {
  const tier = stack.signals_fired >= 2 ? "S" : stack.signals_fired === 1 ? "A" : "B";
  const tierColor = tier === "S" ? "#FF4D9D" : tier === "A" ? "#A855F7" : "rgba(238, 233, 245, 0.4)";
  const tierLabel = tier === "S" ? "TIER S · CONVICTION STACK" : tier === "A" ? "TIER A · CONVICTION STACK" : "CONVICTION STACK · WATCHING";

  return (
    <div className="mt-6 bg-white border border-mb-border rounded-lg p-6 relative overflow-hidden" data-testid="conviction-stack-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <span
            className="font-mono-data font-extrabold text-3xl"
            style={{ color: tierColor, textShadow: `0 0 16px ${tierColor}66` }}
          >
            {stack.score}<span className="text-lg text-neutral-500">/9</span>
          </span>
          <div>
            <div className="label-uppercase" style={{ color: tierColor, letterSpacing: "0.16em" }}>{tierLabel}</div>
            <div className="text-xs text-neutral-500 mt-0.5 font-mono-data">{stack.signals_fired} of 3 signals firing</div>
          </div>
        </div>
        <div className="text-xs text-neutral-500 max-w-md leading-relaxed">
          What the chart can&apos;t show you — insider buying, short-squeeze pressure, and upcoming catalysts. When two or more align with the technical coil, that&apos;s a Tier S Moonshot.
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <SignalTile icon={Users} label="Insider Buying" sig={stack.insider} accent="emerald" />
        <SignalTile icon={Flame} label="Squeeze Pressure" sig={stack.squeeze} accent="pink" />
        <SignalTile icon={CalendarClock} label="Catalyst Window" sig={stack.catalyst} accent="amber" />
      </div>

      {stack.insider?.buys?.length > 0 && (
        <details className="mt-4 group" data-testid="insider-buys-detail">
          <summary className="cursor-pointer label-uppercase text-[10px] tracking-wider text-neutral-500 hover:text-mb-emerald transition select-none py-1">
            ▾ Recent insider purchases ({stack.insider.buys.length})
          </summary>
          <div className="mt-2 space-y-1.5">
            {stack.insider.buys.slice(0, 5).map((b, i) => (
              <div key={i} className="flex items-center justify-between text-xs font-mono-data p-2 rounded bg-neutral-50 border border-mb-border">
                <div className="min-w-0 flex-1">
                  <span className="text-mb-ink font-bold">{b.name}</span>
                  <span className="text-neutral-500 ml-2">· {b.relation}</span>
                </div>
                <div className="text-right ml-3 shrink-0">
                  <div className="text-mb-emerald font-bold">${fmtUsd(b.value_usd)}</div>
                  <div className="text-neutral-400 text-[10px]">{b.date}</div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function SignalTile({ icon: Icon, label, sig, accent }) {
  if (!sig) return null;
  const accentMap = {
    emerald: { fg: "#4ADE80", glow: "rgba(74, 222, 128, 0.30)" },
    pink:    { fg: "#FF4D9D", glow: "rgba(255, 77, 157, 0.4)" },
    amber:   { fg: "#F5A623", glow: "rgba(245, 166, 35, 0.4)" },
  };
  const a = accentMap[accent] || accentMap.emerald;
  const lit = sig.fired;
  return (
    <div
      className={`rounded-lg p-4 border transition-all ${lit ? "" : "opacity-50"}`}
      style={{
        backgroundColor: "var(--mb-surface-elevated)",
        borderColor: lit ? a.fg : "var(--mb-border)",
        boxShadow: lit ? `0 0 24px -12px ${a.glow}, inset 0 1px 0 ${a.glow}` : "none",
      }}
      data-testid={`conviction-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: a.fg }} />
          <span className="label-uppercase text-[10px] tracking-wider" style={{ color: lit ? a.fg : "var(--mb-ink-muted)" }}>{label}</span>
        </div>
        <span className="font-mono-data text-xs font-bold" style={{ color: lit ? a.fg : "var(--mb-ink-subtle)" }}>{sig.score}/3</span>
      </div>
      <div className="font-mono-data text-sm text-mb-ink leading-snug">{sig.headline}</div>
      {lit && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono-data font-bold" style={{ color: a.fg }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: a.fg, boxShadow: `0 0 6px ${a.fg}` }} />
          FIRING
        </div>
      )}
    </div>
  );
}

function fmtUsd(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n/1_000).toFixed(0)}k`;
  return `${n}`;
}

